import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  styled,
  Popover,
  TextField,
  IconButton,
} from "@mui/material";
import { useRbac } from "../../../hooks/RbacContext";
import Tab from "../../../components/Tab/Tab";
import Tabs from "../../../components/Tabs/Tabs";
import TabPanel from "../../../components/TabPanel/TabPanel";
import Button from "../../../components/Button/Button";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PersonIcon from "@mui/icons-material/Person";
import FlagIcon from "@mui/icons-material/Flag";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ProjectActivity from "../ProjectActivity/ProjectActivity";
import AllMilestoneByProject from "../ProjectMilestone/AllMilestoneByProject";
import AllResourceByProject from "../../ProjectResourceMapping/ResourceByProject/AllResourceByProject";
import { useProjectApi } from "../../../hooks/useProjectApi";
import {
  CommentsHistoryPopover,
  commentPopoverProps,
  closeButtonStyles,
} from "../../../components/Table/TimesheetTableStyles";
import { useAllProjectMilestoneViewModel } from "../../ProjectMilestone/ProjectMilestoneAll/AllProjectMilestoneViewModel.js";
import { useProjectMilestoneApi } from "../../../hooks/useProjectMilestone.js";
import { format } from "date-fns";

const MessageButton = styled(IconButton)(({ theme }) => ({
  width: "28px",
  height: "28px",
  borderRadius: "4px",
  padding: "6px",
  backgroundColor: "#F8F9FA",
  transition: "all 0.2s ease",
  position: "relative",

  "&:hover": {
    backgroundColor: "rgba(63, 73, 167, 0.04)",
  },

  "& .message-icon": {
    fontSize: "16px",
    color: "#3F49A7",
    transition: "all 0.2s ease",
  },

  "& .message-indicator": {
    position: "absolute",
    top: "2px",
    right: "2px",
    width: "6px",
    height: "6px",
    backgroundColor: "#3F49A7",
    borderRadius: "50%",
    boxShadow: "0 1px 2px rgba(63, 73, 167, 0.2)",
  },
}));

const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const CommentStyles = styled("div")(({ theme }) => ({
  ".comment-bubble": {
    "&.owner": {
      backgroundColor: "#E8F5E9",
      marginRight: "auto",
      borderLeft: "2px solid #4CAF50",
      "&::before": {
        content: '""',
        position: "absolute",
        left: "-1px",
        top: 0,
        bottom: 0,
        width: "2px",
        background: "linear-gradient(to bottom, #4CAF50, rgba(76,175,80,0.4))",
        borderRadius: "2px",
      },
      "& .owner-name": {
        color: "#4CAF50",
      },
    },
    "&.resource-manager": {
      backgroundColor: "#FFF3E0",
      marginRight: "auto",
      borderLeft: "2px solid #FF9800",
      "&::before": {
        content: '""',
        position: "absolute",
        left: "-1px",
        top: 0,
        bottom: 0,
        width: "2px",
        background: "linear-gradient(to bottom, #FF9800, rgba(255,152,0,0.4))",
        borderRadius: "2px",
      },
      "& .resource-manager-name": {
        color: "#FF9800",
      },
    },
  },
}));

const renderStatusWithDate = (status, timestamp) => {
  let formattedDate = "-";

  if (timestamp) {
    const parsedDate = new Date(Number(timestamp));
    if (!isNaN(parsedDate.getTime())) {
      formattedDate = format(parsedDate, "MMM d, yyyy, h:mm a");
    } else {
      console.error("Invalid timestamp:", timestamp);
    }
  }
  // Use theme for backgroundColor
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={(theme) => ({
          width: "7rem",
          height: "2rem",
          borderRadius: "4px",
          backgroundColor:
            status === "Published" ||
            status === "Rejected" ||
            status === "Approved"
              ? theme.palette.grey[200]
              : undefined,

          border: "1px solid #EFEBEF",
        })}
      >
        <Typography
          sx={(theme) => ({
            fontSize: "0.9rem",
            fontWeight: 500,
            color:
              status === "Published" ? theme.palette.text.primary : undefined,
            px: status === "Published" ? 1 : undefined,
            borderRadius: status === "Published" ? "4px" : undefined,
          })}
        >
          {`${status === "Published" ? "Requested" : status}`}
        </Typography>
      </Box>

      {formattedDate !== "-" && (
        <Box display="flex" gap={0.2}>
          <Typography
            variant="body2"
            sx={{ fontSize: "11px", marginTop: "2px" }}
            color="text.secondary"
          >
            {`
              ${
                status === "Published"
                  ? `Approval Requested on: ${formattedDate}`
                  : status === "Rejected"
                  ? `Approval Rejected on: ${formattedDate}`
                  : `${status} on: ${formattedDate}`
              }`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const MilestonePublish = ({ setShowAddButton }) => {
  const { id } = useParams();
  const { hasProjectPermission, permissions } = useRbac();
  const [value, setValue] = React.useState(0);
  const { getProjectById, getProjectCommentHistory, addProjectComment } =
    useProjectApi();
  const { updateProjectMilestoneStatus, getProjectMilestonesByProjectId } =
    useProjectMilestoneApi();
  const { projectMilestones, refetch } = useAllProjectMilestoneViewModel();
  const [projectDetails, setProjectDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [commentAnchorEl, setCommentAnchorEl] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);
  const [isPublished, setIsPublished] = useState({});
  const [isPending, setIsPending] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [mostRecentStatus, setMostRecentStatus] = useState("");
  const [showStatusLabel, setShowStatusLabel] = useState(false);
  const [mostRecentDate, setMostRecentDate] = useState(null);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const hasMilestoneAccess = hasProjectPermission(
    parseInt(id),
    "milestones",
    "view"
  );

  const isPrimaryManager = permissions?.projectRoles?.find((projectRole) => {
    const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
      ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
          parseInt(id.trim())
        )
      : [];
    return (
      parseInt(id) === parseInt(projectRole?.project_id) &&
      (parseInt(projectRole.role?.role_id) ===
        parseInt(process.env.REACT_APP_PM_ROLE_ID) ||
        superAdminRoleIds.includes(parseInt(projectRole.role?.role_id)))
    );
  });

  const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
        parseInt(id.trim())
      )
    : [];
  console.log(
    "superAdminRoleIds",
    superAdminRoleIds,
    permissions?.globalRole?.role_id
  );
  const isSuperAdmin = superAdminRoleIds.includes(
    parseInt(permissions?.globalRole?.role_id)
  );

  const isProjectOwner = permissions?.projectRoles?.find(
    (projectRole) =>
      parseInt(id) === parseInt(projectRole?.project_id) &&
      parseInt(projectRole.role?.role_id) ===
        parseInt(process.env.REACT_APP_PO_ROLE_ID)
  );
  console.log("showRequestToApproveButton", isPrimaryManager, isSuperAdmin);

  useEffect(() => {
    fetchProjectDetails();
  }, [id, projectMilestones]);

  const fetchProjectDetails = async () => {
    try {
      console.log("projectMilestonePendingDetails");

      const projectMilestones = await getProjectMilestonesByProjectId(id);

      const projectMilestonePendingDetails = projectMilestones?.data?.filter(
        (milestone) => milestone?.publish_type === "PENDING"
      );

      const projectMilestonePublishedDetails = projectMilestones?.data?.filter(
        (milestone) => milestone?.publish_type === "PUBLISHED"
      );
      const projectMilestoneApprovedDetails = projectMilestones?.data?.filter(
        (milestone) => milestone?.publish_type === "APPROVED"
      );
      const projectMilestoneRejectedDetails = projectMilestones?.data?.filter(
        (milestone) => milestone?.publish_type === "REJECTED"
      );
      const hasPending = projectMilestonePendingDetails?.length > 0;
      const hasPublished = projectMilestonePublishedDetails?.length > 0;
      const hasApproved = projectMilestoneApprovedDetails?.length > 0;
      const hasRejected = projectMilestoneRejectedDetails?.length > 0;

      setIsPending(hasPending);
      setIsPublished(hasPublished);
      setIsApproved(hasApproved);
      setIsRejected(hasRejected);
      if (!hasPending && hasPublished) setShowAddButton(false);
      const showRequestToApproveButton =
        (isPrimaryManager || isSuperAdmin) && hasPending;
      const showApproveRejectButtons = isProjectOwner && hasPublished;
      setShowStatusLabel(
        !showRequestToApproveButton && !showApproveRejectButtons
      );

      const getLatestMilestone = () =>
        projectMilestones?.data
          ?.filter((m) => m?.last_published_on)
          .sort(
            (a, b) =>
              new Date(b.last_published_on) - new Date(a.last_published_on)
          )[0];

      const latestMilestone = getLatestMilestone();
      if (latestMilestone) {
        const { publish_type, last_published_on } = latestMilestone;
        // Set most recent status and date state
        setMostRecentStatus(
          publish_type?.charAt(0) + publish_type?.slice(1).toLowerCase()
        ); // For converting it into Aaaaa format
        setMostRecentDate(last_published_on);
      }

      const commentHistory = await getProjectCommentHistory(id, "MILESTONE");
      if (commentHistory?.success && commentHistory?.data) {
        setComments(
          commentHistory.data
            .map((comment) => ({
              ...comment,
              type: comment.type || "RESOURCE",
              resource_name: comment.resource_name || "You",
              created_on: comment.created_on || new Date().toISOString(),
            }))
            .reverse()
        );
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  const handleConfirmAction = (type, action) => {
    const modalConfig = {
      open: true,
      title:
        type === "publish"
          ? "Milestone Approval"
          : `${type.charAt(0).toUpperCase() + type.slice(1)} Project Milestone`,
      message:
        type === "publish"
          ? "Are you sure you want to request for milestone approval?"
          : `Are you sure you want to ${type.toLowerCase()} this project milestones?`,
      onConfirm: async () => {
        try {
          setLoading(true);
          await action();
          await fetchProjectDetails();
        } catch (error) {
          console.error(`Error ${type.toLowerCase()}ing project:`, error);
        } finally {
          setLoading(false);
          setConfirmationModal(null);
        }
      },
      colorCode:
        type === "approve"
          ? "#2E7D32"
          : type === "reject"
          ? "#D32F2F"
          : "#1976D2",
    };
    setConfirmationModal(modalConfig);
  };

  const handleCommentClick = async (event) => {
    setCommentAnchorEl(event.currentTarget);
    const commentHistory = await getProjectCommentHistory(id, "MILESTONE");
    if (commentHistory?.success && commentHistory?.data) {
      setComments(
        commentHistory.data
          .map((comment) => ({
            ...comment,
            type: comment.type || "RESOURCE",
            resource_name: comment.resource_name || "You",
            created_on: comment.created_on || new Date().toISOString(),
          }))
          .reverse()
      );
    }
  };

  const handleCommentClose = () => {
    setCommentAnchorEl(null);
  };

  const handleCommentSend = async () => {
    if (!newComment.trim()) return;

    try {
      let commentType = "RESOURCE";
      const isProjectOwner = permissions?.projectRoles?.find(
        (projectRole) =>
          parseInt(id) === parseInt(projectRole?.project_id) &&
          parseInt(projectRole.role?.role_id) ===
            parseInt(process.env.REACT_APP_PO_ROLE_ID)
      );

      const isPM = permissions?.projectRoles?.find(
        (projectRole) =>
          parseInt(id) === parseInt(projectRole?.project_id) &&
          parseInt(projectRole.role?.role_id) ===
            parseInt(process.env.REACT_APP_PM_ROLE_ID)
      );

      const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
        ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
            parseInt(id.trim())
          )
        : [];
      const isSuperAdmin = superAdminRoleIds.includes(
        parseInt(permissions?.globalRole?.role_id)
      );
      if (isProjectOwner) {
        commentType = "OWNER";
      } else if (isSuperAdmin) {
        commentType = "RESOURCE_MANAGER";
      } else if (isPM) {
        commentType = "MANAGER";
      }

      // Create a new comment object
      const result = await addProjectComment(
        id,
        newComment,
        commentType,
        "MILESTONE"
      );

      if (result.success) {
        // Update local comments state with the new comment
        setComments((prevComments) => [...prevComments, result.data]);
        setNewComment("");
      } else {
        console.error("Failed to add comment:", result.error);
      }
    } catch (error) {
      console.error("Error handling comment:", error);
    }
  };

  const handlePublish = () => {
    handleConfirmAction("publish", async () => {
      try {
        setLoading(true);
        // Format comments as simple strings
        const newComments = comments
          .filter((comment) => !comment.id)
          .map((c) => c.comment);
        await updateProjectMilestoneStatus(
          id,
          "PUBLISHED",
          newComments,
          true,
          "MANAGER"
        );
        await refetch();
      } catch (error) {
        console.error("Error publishing project:", error);
      } finally {
        setLoading(false);
        setConfirmationModal(null);
      }
    });
  };

  const handleApprove = () => {
    handleConfirmAction("approve", async () => {
      try {
        setLoading(true);
        // Format comments as simple strings
        const newComments = comments
          .filter((comment) => !comment.id)
          .map((c) => c.comment);
        await updateProjectMilestoneStatus(
          id,
          "APPROVED",
          newComments,
          true,
          "OWNER"
        );
        await fetchProjectDetails();
      } catch (error) {
        console.error("Error approving project:", error);
      } finally {
        setLoading(false);
        setConfirmationModal(null);
      }
    });
  };

  const handleReject = () => {
    handleConfirmAction("reject", async () => {
      try {
        setLoading(true);
        // Format comments as simple strings
        const newComments = comments
          .filter((comment) => !comment.id)
          .map((c) => c.comment);
        await updateProjectMilestoneStatus(
          id,
          "REJECTED",
          newComments,
          true,
          "OWNER"
        );
        await fetchProjectDetails();
      } catch (error) {
        console.error("Error rejecting project:", error);
      } finally {
        setLoading(false);
        setConfirmationModal(null);
      }
    });
  };

  const handleKeyPress = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleCommentSend();
    }
  };

  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return "-";
      let date;
      if (!isNaN(timestamp)) {
        const timestampNum = Number(timestamp);
        date = new Date(
          timestampNum.toString().length > 10
            ? timestampNum
            : timestampNum * 1000
        );
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) return "-";

      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  const getCommentIcon = (type) => {
    switch (type?.toUpperCase()) {
      case "OWNER":
        return (
          <SupervisorAccountIcon
            className="user-icon"
            sx={{ color: "#4CAF50" }}
          />
        );
      case "MANAGER":
        return (
          <SupervisorAccountIcon
            className="user-icon"
            sx={{ color: "#5F80F6" }}
          />
        );
      case "RESOURCE MANAGER":
        return (
          <SupervisorAccountIcon
            className="user-icon"
            sx={{ color: "#FF9800" }}
          />
        );
      default:
        return <ChatIcon className="user-icon" />;
    }
  };

  const determineCommentPosition = (commentType) => {
    if (isProjectOwner) {
      return commentType === "OWNER" ? "resource" : "manager";
    } else {
      return commentType === "OWNER" ? "manager" : "resource";
    }
  };

  if (!hasMilestoneAccess && !isSuperAdmin) {
    return (
      <Typography color="error" sx={{ p: 3 }}>
        You don't have permission to view project milestones
      </Typography>
    );
  }

  return (
    <div sx={{ width: "100%", px: "0px", py: "0px" }}>
      <CommentStyles>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <Box sx={{ maxWidth: "90%" }}>
            {/* Show status label only if action buttons are NOT shown */}
            {showStatusLabel &&
              renderStatusWithDate(mostRecentStatus, mostRecentDate)}
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Request to Approve button for Primary Manager or Super Admin if pending exists */}
            {(isPrimaryManager || isSuperAdmin) && isPending && (
              <>
                <Button
                  className="action-button publish-button"
                  onClick={handlePublish}
                  disabled={loading}
                  variant="contained"
                  size="small"
                >
                  Request for Approval
                </Button>
              </>
            )}

            {/* Approve/Reject buttons for Project Owner if published exists */}
            {isProjectOwner && isPublished && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      className="action-button approve-button"
                      onClick={handleApprove}
                      disabled={loading}
                      variant="contained"
                      size="small"
                    >
                      Approve
                    </Button>
                    <Button
                      className="action-button reject-button"
                      onClick={handleReject}
                      disabled={loading}
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#D32F2F !important",
                        color: "#FFFFFF !important",
                        "&:hover": {
                          backgroundColor: "#B71C1C !important",
                          color: "#FFFFFF !important",
                        },
                      }}
                    >
                      Reject
                    </Button>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    {`Approval Requested on: ${formatDate(mostRecentDate)}`}
                  </Typography>
                </Box>
              </>
            )}

            {/* Comments Button */}
            {(isApproved || isRejected || isPending || isPublished) && (
              <MessageButton onClick={handleCommentClick}>
                {comments.length > 0 ? (
                  <>
                    <ChatIcon className="message-icon" />
                    <span className="message-indicator" />
                  </>
                ) : (
                  <ChatBubbleOutlineIcon
                    sx={{ fontSize: "16px", color: "rgba(63, 73, 167, 0.5)" }}
                  />
                )}
              </MessageButton>
            )}
          </Box>
        </Box>

        {/* Confirmation Modal */}
        {confirmationModal && (
          <ConfirmationModal
            open={confirmationModal.open}
            title={confirmationModal.title}
            message={confirmationModal.message}
            onClose={() => setConfirmationModal(null)}
            onConfirm={confirmationModal.onConfirm}
            buttonText={
              confirmationModal.title === "Milestone Approval"
                ? "Request"
                : confirmationModal.title.split(" ")[0]
            }
            colorCode={confirmationModal.colorCode}
          />
        )}

        {/* Comments Popover */}
        {
          <Popover
            open={Boolean(commentAnchorEl)}
            anchorEl={commentAnchorEl}
            onClose={handleCommentClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              ...commentPopoverProps,
              sx: {
                ...commentPopoverProps.sx,
                backgroundColor: "transparent",
              },
            }}
          >
            <CommentsHistoryPopover>
              <div className="comments-header">
                <Typography variant="h6">
                  <ChatIcon className="icon" />
                  Comments
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleCommentClose}
                  sx={closeButtonStyles}
                >
                  <CloseIcon />
                </IconButton>
              </div>

              <div className="comment-list">
                {comments && comments.length > 0 ? (
                  comments.map((comment, index) => (
                    <div
                      key={index}
                      className={`comment-bubble ${determineCommentPosition(
                        comment.type
                      )}`}
                    >
                      <Typography className="comment-text">
                        {comment.comment}
                      </Typography>
                      <div className="comment-meta">
                        <span className="user-name">
                          {getCommentIcon(comment.type)}
                          {comment.resource_name}
                        </span>
                        <span className="comment-date">
                          <AccessTimeIcon className="time-icon" />
                          {formatDate(comment.created_on)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-comments">
                    <ChatBubbleOutlineIcon className="icon" />
                    <Typography className="text">
                      No comments available for this project
                    </Typography>
                  </div>
                )}
              </div>

              <div className="comment-input-container">
                <TextField
                  className="comment-input"
                  multiline
                  maxRows={4}
                  placeholder="Type a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={handleKeyPress}
                  variant="outlined"
                  fullWidth
                />
                <IconButton
                  className="send-button"
                  onClick={handleCommentSend}
                  disabled={!newComment.trim()}
                >
                  <SendIcon
                    sx={{ fontSize: "16px", transform: "rotate(-45deg)" }}
                  />
                </IconButton>
              </div>
            </CommentsHistoryPopover>
          </Popover>
        }
      </CommentStyles>
    </div>
  );
};

export default MilestonePublish;
